// ThemePicker - light / dark / follow-the-OS picker.
//
// Successor of v1 modules/ThemeToggle.js. v1 was a two-state switch, which
// cannot express the third state the logic always had ("nothing stored yet =
// follow the OS"); v1 in fact destroyed that state on the first page load by
// persisting the resolved theme right away. The picker names all three, so
// "follow the OS" is a thing the user can see and choose again after picking
// light or dark. See docs/porting.md for the full reasoning.
//
// Applying and persisting lives in ../theme (shared with the render-blocking
// boot snippet). This module is only the control.
//
// Built on the existing Dropdown + Toggle contract, so positioning (flip,
// shift, anchoring via @floating-ui/dom) and closing on an outside click come
// from there rather than being reimplemented:
//
//   <div class="theme-picker" data-ms-theme-picker>
//     <button class="theme-picker__trigger" type="button"
//       data-toggle="self, next" data-toggle-expand aria-expanded="false"
//       aria-haspopup="true" data-ms-theme-picker-trigger>
//       <i class="theme-picker__icon far fa-sun-bright" aria-hidden="true" data-ms-theme-icon-for="light"></i>
//       <i class="theme-picker__icon far fa-moon" aria-hidden="true" data-ms-theme-icon-for="dark"></i>
//       <i class="theme-picker__icon far fa-circle-half-stroke" aria-hidden="true" data-ms-theme-icon-for="auto"></i>
//       <span data-ms-theme-picker-label>Podľa systému</span>
//     </button>
//     <div class="dropdown" data-dropdown data-toggle-expand aria-expanded="false">
//       <div class="dropdown__wrap" role="radiogroup" aria-label="Vzhľad">
//         <button class="dropdown__item" type="button" role="radio"
//           data-ms-theme-mode="light">Svetlá</button>
//         ...
//       </div>
//     </div>
//   </div>
//
// The module carries no strings: it mirrors the selected option's text into the
// trigger, so labels stay server-rendered and localized by the consumer. Leave
// out the label element for an icon-only trigger - the module then puts the
// option text in aria-label instead. Note data-toggle-expand belongs on the
// menu as well as the trigger; Toggle reads it off each target.

import { doToggle, isToggled } from './toggle';
import {
  DARK_MEDIA_QUERY,
  THEME_CHANGE_EVT,
  applyTheme,
  getAppliedTheme,
  getStoredMode,
  getTheme,
  resolveTheme,
  setTheme,
  type Theme,
  type ThemeMode,
} from '../theme';

export interface ThemePickerConfig {
  /** Selector of the picker wrappers. */
  selector: string;
  /**
   * Class marking the selected option. Default suits a `.dropdown` menu; a
   * single picker can override it with `data-ms-theme-picker-checked-class`
   * (e.g. `is-active` for segments in a `.btn-group--tab`).
   */
  checkedClass: string;
}

const defaultConfig: Omit<ThemePickerConfig, 'selector'> = {
  checkedClass: 'dropdown__item--checked',
};

const OPTION_SELECTOR = '[data-ms-theme-mode]';
const TRIGGER_SELECTOR = '[data-ms-theme-picker-trigger]';
const LABEL_SELECTOR = '[data-ms-theme-picker-label]';
const MENU_SELECTOR = '[data-dropdown]';

const isMode = (value: string | null): value is ThemeMode =>
  value === 'light' || value === 'dark' || value === 'auto';

export default class ThemePicker {
  config: ThemePickerConfig;

  private mediaQuery: MediaQueryList | null = null;

  constructor(selector = '[data-ms-theme-picker]', config: Partial<ThemePickerConfig> = {}) {
    this.config = { ...defaultConfig, selector, ...config };

    this._init();
  }

  /** Re-sync after the DOM changed (Turbo navigation, lazily rendered menus). */
  update = (): void => {
    this._syncAll();
  };

  destroy = (): void => {
    document.removeEventListener('click', this._onClick);
    document.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener(THEME_CHANGE_EVT, this._onThemeChange);
    this.mediaQuery?.removeEventListener('change', this._onSystemChange);
    this.mediaQuery = null;
  };

  /** The theme in effect right now. */
  getTheme = (): Theme => getTheme();

  /** The mode the user picked, or `auto` when they never did. */
  getMode = (): ThemeMode => getStoredMode();

  /** Pick a mode programmatically. `auto` clears the stored choice. */
  setMode = (mode: ThemeMode): Theme => setTheme(mode);

  private _init = (): void => {
    // Fallback only: on a real page the boot snippet (dist/theme.js) already
    // set the attribute in <head>. Applying unconditionally would fight
    // whatever legitimately owns it - Storybook's "Téma" toolbar, or a
    // server-rendered value.
    if (!getAppliedTheme()) {
      applyTheme(resolveTheme(getStoredMode()));
    }

    this._syncAll();

    // Delegated, so pickers rendered after init work too (the one inside the
    // lazily-rendered mobile mega menu, or anything after a Turbo navigation).
    document.addEventListener('click', this._onClick);
    document.addEventListener('keydown', this._onKeydown);
    document.addEventListener(THEME_CHANGE_EVT, this._onThemeChange);

    if (typeof window.matchMedia === 'function') {
      this.mediaQuery = window.matchMedia(DARK_MEDIA_QUERY);
      this.mediaQuery.addEventListener('change', this._onSystemChange);
    }
  };

  private _pickers = (): HTMLElement[] => [
    ...document.querySelectorAll<HTMLElement>(this.config.selector),
  ];

  private _options = (picker: HTMLElement): HTMLElement[] => [
    ...picker.querySelectorAll<HTMLElement>(OPTION_SELECTOR),
  ];

  private _syncAll = (): void => {
    const mode = getStoredMode();

    this._pickers().forEach(picker => this._sync(picker, mode));
  };

  private _sync = (picker: HTMLElement, mode: ThemeMode): void => {
    const options = this._options(picker);
    const selected = options.find(option => option.dataset.msThemeMode === mode) ?? null;
    // Per-picker override (one class name), because the right marker depends on
    // what the options are: .dropdown__item--checked in a menu, .is-active for
    // segments in a .btn-group--tab. Same idea as the per-container overrides in
    // modules/gallery.ts.
    const checkedClass = picker.dataset.msThemePickerCheckedClass || this.config.checkedClass;

    options.forEach(option => {
      const isSelected = option === selected;

      option.classList.toggle(checkedClass, isSelected);
      option.setAttribute('aria-checked', String(isSelected));
      // Roving tabindex: the radiogroup is one tab stop, arrows move within.
      option.tabIndex = isSelected ? 0 : -1;
    });

    // Which of the trigger's icons is visible is CSS's job - the trigger holds
    // one [data-ms-theme-icon-for] per mode and this attribute picks it (see
    // modules/_theme-picker.scss). Swapping classes on the icon element cannot
    // work: Font Awesome runs in SVG-with-JS mode and replaces the <i> with a
    // pre-rendered <svg>, so a later class change would not re-draw the glyph.
    // v1's switch showed/hid its two icons the same way.
    picker.dataset.msThemeModeActive = mode;

    const trigger = picker.querySelector<HTMLElement>(TRIGGER_SELECTOR);

    // No matching option (a picker offering only light/dark, say) - there is
    // no label to mirror, so leave the trigger's text as the markup had it.
    if (!selected || !trigger) {
      return;
    }

    const label = selected.dataset.msThemeLabel ?? selected.textContent?.trim() ?? '';
    const labelEl = trigger.querySelector<HTMLElement>(LABEL_SELECTOR);

    if (labelEl) {
      labelEl.textContent = label;
    } else if (label) {
      // Icon-only trigger: the icons are aria-hidden, so the name has to come
      // from aria-label or the button is announced as unlabelled.
      trigger.setAttribute('aria-label', label);
    }
  };

  private _menu = (picker: HTMLElement): HTMLElement | null =>
    picker.querySelector<HTMLElement>(MENU_SELECTOR);

  /** Close the menu through the Toggle contract that opened it. */
  private _close = (picker: HTMLElement, { focusTrigger = false } = {}): void => {
    const menu = this._menu(picker);
    const trigger = picker.querySelector<HTMLElement>(TRIGGER_SELECTOR);

    // The trigger is data-toggle="self, next", so both it and the menu carry
    // aria-expanded and both have to come back down.
    [trigger, menu].forEach(target => {
      if (target && isToggled(target)) {
        doToggle({ target, expand: true, state: false, trigger });
      }
    });

    if (focusTrigger) {
      trigger?.focus();
    }
  };

  private _onClick = (e: Event): void => {
    const option = (e.target as Element | null)?.closest<HTMLElement>(OPTION_SELECTOR);
    const mode = option?.dataset.msThemeMode ?? null;

    if (!option || !isMode(mode)) {
      return;
    }

    const picker = option.closest<HTMLElement>(this.config.selector);
    if (!picker) {
      return;
    }

    // The contract is a <button type="button">, but a consumer wrapping the
    // picker in a form (or using an <a>) shouldn't submit or navigate.
    e.preventDefault();

    setTheme(mode);
    this._close(picker, { focusTrigger: true });
  };

  private _onKeydown = (e: KeyboardEvent): void => {
    const option = (e.target as Element | null)?.closest<HTMLElement>(OPTION_SELECTOR);

    if (!option || e.metaKey || e.ctrlKey || e.altKey) {
      return;
    }

    const picker = option.closest<HTMLElement>(this.config.selector);
    if (!picker) {
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      this._close(picker, { focusTrigger: true });
      return;
    }

    const options = this._options(picker);
    const current = options.indexOf(option);
    let next = -1;

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = (current + 1) % options.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        next = (current - 1 + options.length) % options.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = options.length - 1;
        break;
      default:
        return;
    }

    const target = options[next];
    const mode = target?.dataset.msThemeMode ?? null;

    if (!target || !isMode(mode)) {
      return;
    }

    e.preventDefault();

    // Selection follows focus, as ARIA prescribes for a radiogroup - which
    // for a theme picker doubles as a live preview while arrowing through.
    setTheme(mode);
    target.focus();
  };

  private _onThemeChange = (): void => {
    this._syncAll();
  };

  // Only meaningful while the user has no explicit choice. v1 applied the new
  // OS value unconditionally *and* wrote it to storage, so a visitor who had
  // deliberately picked light got flipped at sunset - and their choice was
  // deleted in the process.
  private _onSystemChange = (e: MediaQueryListEvent): void => {
    if (getStoredMode() !== 'auto') {
      return;
    }

    applyTheme(e.matches ? 'dark' : 'light', { suppressTransitions: true });
    this._syncAll();
  };
}
